import React, { useEffect, useState } from "react";
import { Sparkles, ShoppingCart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CreditWheel from "./CreditWheel";
import { fmtCAD, fmtInt, creditPrice } from "@/lib/billing";

/* Build Your Own Pack — two 500-step wheels, live price + approx-results, one Add to Cart. */
export default function BuildYourOwnDialog({ open, onOpenChange, pricing, onAdd }) {
  const cfg = pricing?.custom || { step: 500, min: 0, max: 20000, min_total: 500, default: 5000 };
  const [enr, setEnr] = useState(cfg.default);
  const [scr, setScr] = useState(cfg.default);

  useEffect(() => {
    if (open) { setEnr(cfg.default); setScr(cfg.default); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = enr + scr;
  const price = creditPrice(pricing, "enrichment", enr) + creditPrice(pricing, "scrape", scr);
  const canAdd = total >= (cfg.min_total || 500);

  const add = () => { onAdd({ enrichment: enr, scrape: scr }); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="build-your-own-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="h-5 w-5 text-[#C8102E]" /> Build your own pack
          </DialogTitle>
          <DialogDescription>
            Dial in exactly the credits you need. Either dial can sit at zero — a custom pack just needs
            at least {fmtInt(cfg.min_total)} credits in total.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-3">
          <CreditWheel pool="enrichment" label="Enrichment" value={enr} onChange={setEnr}
            step={cfg.step} min={cfg.min} max={cfg.max} pricing={pricing} ratios={pricing?.results} />
          <CreditWheel pool="scrape" label="Scrape" value={scr} onChange={setScr}
            step={cfg.step} min={cfg.min} max={cfg.max} pricing={pricing} ratios={pricing?.results} />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Pack subtotal</div>
            <div className="text-xl font-bold text-slate-900 tabular-nums" data-testid="build-subtotal">{fmtCAD(price)}</div>
            <div className="text-[11px] text-slate-500">before tax · {fmtInt(total)} credits</div>
          </div>
          <Button onClick={add} disabled={!canAdd} className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }} data-testid="build-add-to-cart">
            <ShoppingCart className="h-4 w-4 mr-2" /> Add to cart
          </Button>
        </div>
        {!canAdd ? (
          <p className="text-[11px] text-amber-700 -mt-1" data-testid="build-min-note">
            Add at least {fmtInt(cfg.min_total)} credits to continue.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
